import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Sparkles, Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useShiftPlusOffer } from "@/components/FirstMonthOffer";

function NextChapter() {
  const offer = useShiftPlusOffer();
  const { user } = useAuth();
  const showOffer = offer && !user?.is_member && !user?.member_until;

  return (
    <div
      className={`mt-12 sm:mt-16 border rounded-sm p-6 sm:p-8 ${showOffer ? "border-accent bg-accent/5" : "border-border"}`}
      data-testid={showOffer ? "result-first-month-offer" : "result-next-chapter"}
    >
      <div className="eyebrow mb-3 flex items-center gap-2">
        {showOffer && <Sparkles className="h-3 w-3 text-accent" />}
        {showOffer ? "Your next chapter · new member offer" : "The next chapter"}
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl mb-3 leading-snug">
        {showOffer ? (
          <>Your shift is asking for structure. Start Shift+ for <span className="italic text-accent">${offer.shift_plus_first_month_usd}</span> your first month.</>
        ) : (
          "Your shift is asking for structure."
        )}
      </h3>
      <p className="text-muted-foreground mb-6 leading-relaxed text-sm sm:text-base">
        The Shift Room is where the reading becomes daily practice — weekly transmissions,
        challenges, the private Discord, and a room of people becoming with you.
        {showOffer && ` Then $${offer.shift_plus_monthly_usd}/mo after your first month.`}
      </p>
      <Link to={showOffer ? "/membership?plan=shift_society" : "/membership"} className="block sm:inline-block">
        <Button className="w-full sm:w-auto btn-pill font-mono tracking-widest uppercase text-xs gap-2 h-12" data-testid="result-join-room-btn">
          {showOffer ? `Claim my $${offer.shift_plus_first_month_usd} first month` : "Enter the Shift Room"} <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

export default function ShiftResult() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api
      .get(`/assessment/result/${id}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error("Failed to load shift result:", err);
        setLoadError(true);
      });
  }, [id]);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/shift-reader/result/${id}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share your shift.");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `My Shift: ${data.profile.shift_name}`,
          text: data.profile.closing_line,
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      copyLink();
    }
  };

  if (loadError) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center px-4" data-testid="shift-result-error">
        <div className="text-center max-w-md">
          <div className="eyebrow mb-3 text-destructive">Not found</div>
          <h1 className="font-serif text-3xl mb-4">This reading isn't available.</h1>
          <p className="text-muted-foreground mb-6">The link may be broken or the reading may have expired.</p>
          <Link to="/shift-reader" className="font-mono text-xs uppercase tracking-widest text-accent hover:underline">
            Take a new reading →
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  const p = data.profile;

  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain" data-testid="shift-result-page">
      <div className="mx-auto max-w-4xl px-5 sm:px-6 py-10 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="eyebrow mb-4 text-accent">Your Shift Profile</div>

          {/* Share card */}
          <div
            ref={cardRef}
            className="border border-accent/40 bg-secondary/30 backdrop-blur-sm p-6 sm:p-12 rounded-sm relative overflow-hidden"
            data-testid="result-share-card"
          >
            <div className="absolute top-4 right-4 opacity-60">
              <Sparkles className="h-4 w-4 text-accent" />
            </div>
            <div className="font-mono text-[10px] sm:text-xs tracking-[0.32em] uppercase text-muted-foreground mb-3">
              Your Shift
            </div>
            <h1 className="font-serif text-[2.5rem] leading-[0.95] sm:text-6xl lg:text-7xl tracking-tight mb-8 sm:mb-10 text-accent italic break-words">
              {p.shift_name}
            </h1>

            <div className="grid grid-cols-2 gap-5 sm:gap-6 mb-8 sm:mb-10">
              <Row label="Current Frequency" value={p.current_frequency} />
              <Row label="Old Timeline" value={p.old_timeline} />
              <Row label="Emerging Timeline" value={p.emerging_timeline} />
              <Row label="Season" value={p.shift_name.replace("THE ", "")} />
            </div>

            <div className="hairline pt-6 space-y-6">
              <Block label="Core Lesson" body={p.core_lesson} />
              <Block label="Next Move" body={p.next_move} />
            </div>

            <div className="mt-8 sm:mt-10 pt-6 hairline">
              <p className="font-serif italic text-base sm:text-2xl leading-snug">
                "{p.closing_line}"
              </p>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <Button
              onClick={share}
              className="w-full sm:w-auto btn-pill font-mono tracking-widest uppercase text-xs gap-2 h-12"
              data-testid="result-share-btn"
            >
              <Download className="h-4 w-4" /> Share my Shift
            </Button>
            <Button
              variant="outline"
              onClick={copyLink}
              className="w-full sm:w-auto btn-pill font-mono tracking-widest uppercase text-xs gap-2 h-12"
              data-testid="result-copy-link-btn"
            >
              <Copy className="h-4 w-4" /> Copy link
            </Button>
            <Link to="/library" className="w-full sm:w-auto">
              <Button
                variant="ghost"
                className="w-full sm:w-auto btn-pill font-mono tracking-widest uppercase text-xs gap-2 h-12"
                data-testid="result-explore-library-btn"
              >
                Explore the Library <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <NextChapter />
        </motion.div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-muted-foreground mb-1">
        {label}
      </div>
      <div className="font-serif text-lg sm:text-2xl leading-tight break-words">{value}</div>
    </div>
  );
}

function Block({ label, body }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-accent mb-2">
        {label}
      </div>
      <p className="font-serif text-base sm:text-2xl leading-snug">{body}</p>
    </div>
  );
}
